// =============================================================================
// EmailSection.js — Phase 82
//
// Wired to the same backend endpoints the old Settings page used:
//   • GET/PATCH /api/profile/weekly-brief-cadence
//   • GET/PATCH /api/profile/monthly-brief-cadence
//   • GET/PATCH /api/profile/marketing-opt-out
//
// The Figma design shows a 4-row toggle grid. Since Sunday brief and Monthly
// recap have discrete cadence options (weekly/fortnightly/monthly/off), we
// map them to a toggle + "Cadence" hint where the toggle sets 'off' or the
// most recent non-off cadence. Marketing / Product updates are boolean.
// =============================================================================
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import {
  SectionHeader, SettingsCard, Toggle, SelectInput, FieldLabel,
} from './primitives';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailSection() {
  const { session } = useAuth();
  const [sundayCadence, setSundayCadence] = useState('weekly');
  const [monthlyCadence, setMonthlyCadence] = useState('monthly');
  const [marketingOptIn, setMarketingOptIn] = useState(true);
  const [saving, setSaving] = useState({ sunday: false, monthly: false, marketing: false });

  useEffect(() => {
    if (!session?.access_token) return;
    let cancelled = false;
    (async () => {
      try {
        const [w, m, mk] = await Promise.allSettled([
          axios.get(`${BACKEND_URL}/api/profile/weekly-brief-cadence`, {
            headers: { Authorization: `Bearer ${session.access_token}` }, timeout: 10000,
          }),
          axios.get(`${BACKEND_URL}/api/profile/monthly-brief-cadence`, {
            headers: { Authorization: `Bearer ${session.access_token}` }, timeout: 10000,
          }),
          axios.get(`${BACKEND_URL}/api/profile/marketing-opt-out`, {
            headers: { Authorization: `Bearer ${session.access_token}` }, timeout: 10000,
          }),
        ]);
        if (cancelled) return;
        if (w.status === 'fulfilled' && w.value.data?.cadence) setSundayCadence(w.value.data.cadence);
        if (m.status === 'fulfilled' && m.value.data?.cadence) setMonthlyCadence(m.value.data.cadence);
        if (mk.status === 'fulfilled' && typeof mk.value.data?.opt_in === 'boolean') setMarketingOptIn(mk.value.data.opt_in);
      } catch { /* non-fatal — defaults remain */ }
    })();
    return () => { cancelled = true; };
  }, [session?.access_token]);

  const patchSundayCadence = async (next) => {
    const prev = sundayCadence;
    setSundayCadence(next);
    setSaving((s) => ({ ...s, sunday: true }));
    try {
      await axios.patch(`${BACKEND_URL}/api/profile/weekly-brief-cadence`,
        { cadence: next },
        { headers: { Authorization: `Bearer ${session.access_token}` }, timeout: 10000 });
      toast.success('Sunday brief cadence updated.');
    } catch (e) {
      setSundayCadence(prev);
      toast.error(e.response?.data?.detail || 'Could not update cadence.');
    } finally {
      setSaving((s) => ({ ...s, sunday: false }));
    }
  };

  const patchMonthlyCadence = async (next) => {
    const prev = monthlyCadence;
    setMonthlyCadence(next);
    setSaving((s) => ({ ...s, monthly: true }));
    try {
      await axios.patch(`${BACKEND_URL}/api/profile/monthly-brief-cadence`,
        { cadence: next },
        { headers: { Authorization: `Bearer ${session.access_token}` }, timeout: 10000 });
      toast.success('Monthly recap cadence updated.');
    } catch (e) {
      setMonthlyCadence(prev);
      toast.error(e.response?.data?.detail || 'Could not update cadence.');
    } finally {
      setSaving((s) => ({ ...s, monthly: false }));
    }
  };

  const patchMarketing = async (checked) => {
    const prev = marketingOptIn;
    setMarketingOptIn(checked);
    setSaving((s) => ({ ...s, marketing: true }));
    try {
      await axios.patch(`${BACKEND_URL}/api/profile/marketing-opt-out`,
        { opt_in: !!checked },
        { headers: { Authorization: `Bearer ${session.access_token}` }, timeout: 10000 });
      toast.success(checked ? "You'll receive occasional product updates." : "You're opted out of marketing emails.");
    } catch (e) {
      setMarketingOptIn(prev);
      toast.error(e.response?.data?.detail || 'Could not update marketing preference.');
    } finally {
      setSaving((s) => ({ ...s, marketing: false }));
    }
  };

  return (
    <div className="space-y-5" data-testid="settings-email-section">
      <SectionHeader title="Email Preferences" subtitle="Choose what 2120 sends you and how often." />

      <SettingsCard className="divide-y divide-border/60">
        {/* Sunday brief */}
        <div className="px-6 py-5 flex items-start gap-5">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-charcoal">Sunday Brief</p>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              A weekly financial summary delivered every Sunday morning — what happened, what changed, and what to consider this week.
            </p>
            <div className="mt-3 max-w-[240px]">
              <FieldLabel label={`Cadence${saving.sunday ? ' · Saving…' : ''}`} />
              <SelectInput
                value={sundayCadence}
                onChange={patchSundayCadence}
                disabled={saving.sunday}
                testId="settings-sunday-cadence"
                options={[
                  { value: 'weekly',      label: 'Every Sunday' },
                  { value: 'fortnightly', label: 'Every other Sunday' },
                  { value: 'monthly',     label: 'First Sunday of each month' },
                  { value: 'off',         label: 'Off' },
                ]}
              />
            </div>
          </div>
          <Toggle
            checked={sundayCadence !== 'off'}
            onChange={(v) => patchSundayCadence(v ? 'weekly' : 'off')}
            disabled={saving.sunday}
            testId="settings-sunday-toggle"
            ariaLabel="Toggle Sunday brief"
          />
        </div>

        {/* Monthly recap */}
        <div className="px-6 py-5 flex items-start gap-5">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-charcoal">Monthly Recap</p>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              A summary of your financial month — surplus, spending categories, goal progress, and your Clarity Score.
            </p>
            <p className="text-xs text-muted-foreground/70 mt-1.5">Delivered on the 1st of each month.</p>
          </div>
          <Toggle
            checked={monthlyCadence !== 'off'}
            onChange={(v) => patchMonthlyCadence(v ? 'monthly' : 'off')}
            disabled={saving.monthly}
            testId="settings-monthly-toggle"
            ariaLabel="Toggle monthly recap"
          />
        </div>

        {/* Marketing */}
        <div className="px-6 py-5 flex items-start gap-5">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-charcoal">Marketing &amp; product updates</p>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              New features, product news, and things worth knowing.
            </p>
            <p className="text-xs text-muted-foreground/70 mt-1.5">Infrequent — a couple a month at most.</p>
          </div>
          <Toggle
            checked={marketingOptIn}
            onChange={patchMarketing}
            disabled={saving.marketing}
            testId="settings-marketing-toggle"
            ariaLabel="Toggle marketing emails"
          />
        </div>
      </SettingsCard>

      {/* Always-on notice — regulatory emails always send. */}
      <div
        className="rounded-2xl px-5 py-4 bg-offwhite border border-border/50"
        data-testid="settings-emails-always-on"
      >
        <p className="text-xs text-muted-foreground/80 leading-relaxed">
          <span className="font-medium text-charcoal">Always on:</span> transactional emails about your account — password changes, billing receipts, and security alerts — are always sent regardless of these preferences.
        </p>
      </div>

      {(sundayCadence === 'off' && monthlyCadence === 'off') && (
        <div
          className="rounded-2xl px-5 py-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/60"
          data-testid="settings-emails-off-warning"
        >
          <p className="text-xs leading-relaxed text-amber-900 dark:text-amber-200">
            Without these updates, it&apos;s easier to lose track of your financial rhythm. You&apos;ll still see everything whenever you open 2120.
          </p>
        </div>
      )}
    </div>
  );
}
